/**
 * Mock data for Owner Goals Dashboard
 * This provides sample data for the 3 main business goals:
 * 1. Sales Revenue Goal
 * 2. Course Creation Goal
 * 3. Course Distribution Goal
 *
 * Plus a Vision Goal that serves as the overarching objective
 */

export const MOCK_GOALS_DATA = {
  vision: {
    id: 'vision-goal',
    title: 'Build a Thriving Online Education Business',
    description: 'Transform your expertise into a sustainable, profitable online education platform that serves thousands of learners and generates consistent revenue.',
    deadline: '2025-12-31',
    progress: 0.30,
    supportingGoalsCount: 3,
    supportingGoalsCompleted: 0,
  },
  salesRevenue: {
    id: 'sales-revenue',
    type: 'revenue',
    title: 'Sales',
    subtitle: 'Sold in November',
    target: '1 M',
    current: '24 000',
    currency: '€',
    startDate: '2024-06-01',
    deadline: '2025-06-30',
    progress: 0.35,
    analytics: {
      monthlyAverage: 2000,
      lastMonthSales: 2500,
      trend: 'up',
      trendPercentage: 15,
      projectedCompletion: '2026-03-15',
      daysRemaining: 240,
    },
    actionPlan: {
      nextAction: 'Launch 4-6 courses next 6 months / average price point €150, min price point €90 max price point € 300',
      steps: [
        {
          id: 'step-1',
          title: 'Price your courses at €49 average to reach your goal',
          description: 'Adjust pricing strategy to optimize revenue per course',
          completed: false,
          priority: 'high',
          category: 'pricing'
        },
        {
          id: 'step-2',
          title: 'Sell 775 more courses in the next 8 months',
          description: 'Target approximately 97 course sales per month',
          completed: false,
          priority: 'high',
          category: 'sales'
        },
        {
          id: 'step-3',
          title: 'Launch seasonal promotion campaign',
          description: 'Create limited-time offers to boost sales velocity',
          completed: true,
          priority: 'medium',
          category: 'marketing'
        },
        {
          id: 'step-4',
          title: 'Optimize course landing pages for conversion',
          description: 'Improve course descriptions and preview content',
          completed: true,
          priority: 'medium',
          category: 'marketing'
        },
        {
          id: 'step-5',
          title: 'Set up email marketing automation',
          description: 'Create drip campaigns for course promotion',
          completed: true,
          priority: 'high',
          category: 'marketing'
        }
      ]
    },
    inspiration: {
      benchmarks: [
        {
          metric: 'Average course price',
          yourValue: '€35',
          industryAverage: '€49',
          topPerformers: '€79'
        },
        {
          metric: 'Monthly sales',
          yourValue: '€2,000',
          industryAverage: '€4,500',
          topPerformers: '€12,000'
        }
      ],
      successStories: [
        {
          title: 'How Sarah grew from €10k to €100k in 12 months',
          excerpt: 'Learn the strategies that helped Sarah 10x her course revenue...',
          url: 'https://blog.coursio.com/success-story-sarah'
        },
        {
          title: 'Pricing strategies that doubled conversion rates',
          excerpt: 'Discover the psychology behind effective course pricing...',
          url: 'https://blog.coursio.com/pricing-strategies'
        }
      ],
      marketTrends: [
        'Online learning market growing 20% annually',
        'Micro-credentials and skill-based courses in high demand',
        'Video-first content seeing 3x higher engagement'
      ]
    }
  },
  courseCreation: {
    id: 'course-creation',
    type: 'creation',
    title: 'Courses created',
    subtitle: 'Courses created this year so far',
    target: '20',
    current: '12',
    unit: 'courses',
    startDate: '2024-09-01',
    deadline: '2025-08-31',
    progress: 0.48,
    analytics: {
      monthlyAverage: 0.5,
      lastMonthCreated: 1,
      trend: 'down',
      trendPercentage: -2,
      projectedCompletion: '2026-02-01',
      daysRemaining: 240,
      draftCourses: 2,
      publishedCourses: 3
    },
    actionPlan: {
      nextAction: 'Get help from Coursio team to improve content. Add more teachers',
      steps: [
        {
          id: 'step-1',
          title: 'Add 2 more teachers/editors to scale production',
          description: 'Expand your team to increase course creation capacity',
          completed: false,
          priority: 'high',
          category: 'team'
        },
        {
          id: 'step-2',
          title: 'Complete 2 draft courses currently in progress',
          description: 'Finish and publish existing draft content',
          completed: true,
          priority: 'high',
          category: 'production'
        },
        {
          id: 'step-3',
          title: 'Improve course content quality scores',
          description: 'Enhance video quality, add interactive elements, improve assessments',
          completed: true,
          priority: 'medium',
          category: 'quality'
        },
        {
          id: 'step-4',
          title: 'Create content production schedule',
          description: 'Plan quarterly content calendar with milestones',
          completed: true,
          priority: 'medium',
          category: 'planning'
        },
        {
          id: 'step-5',
          title: 'Set up course templates for faster creation',
          description: 'Standardize course structure to streamline production',
          completed: true,
          priority: 'low',
          category: 'efficiency'
        }
      ]
    },
    inspiration: {
      benchmarks: [
        {
          metric: 'Courses per year',
          yourValue: '4-5',
          industryAverage: '8-10',
          topPerformers: '15-20'
        },
        {
          metric: 'Time to create course',
          yourValue: '8 weeks',
          industryAverage: '6 weeks',
          topPerformers: '3-4 weeks'
        }
      ],
      successStories: [
        {
          title: 'Building a course library: From 3 to 30 courses',
          excerpt: 'How one educator scaled content production efficiently...',
          url: 'https://blog.coursio.com/scaling-course-production'
        },
        {
          title: 'The power of course templates and reusable content',
          excerpt: 'Save 50% of production time with smart templates...',
          url: 'https://blog.coursio.com/course-templates'
        }
      ],
      marketTrends: [
        'Modular micro-courses gaining popularity over long-form content',
        'AI-assisted content creation tools reducing production time by 40%',
        'Interactive elements increasing completion rates by 60%'
      ]
    }
  },
  courseDistribution: {
    id: 'course-distribution',
    type: 'distribution',
    title: 'Courses sold',
    subtitle: 'Courses sold this year so far',
    target: '20',
    current: '4',
    unit: 'courses',
    startDate: '2024-03-01',
    deadline: '2025-09-30',
    progress: 0.18,
    analytics: {
      monthlyAverage: 25,
      lastMonthEnrollments: 32,
      trend: 'up',
      trendPercentage: 28,
      projectedCompletion: '2026-01-15',
      daysRemaining: 240,
      invitationsSent: 320,
      invitationsAccepted: 145,
      conversionRate: 0.453
    },
    actionPlan: {
      nextAction: 'New course topic suggestions',
      steps: [
        {
          id: 'step-1',
          title: 'Increase invitation volume to 60 per month',
          description: 'Expand outreach to reach enrollment targets',
          completed: false,
          priority: 'high',
          category: 'outreach'
        },
        {
          id: 'step-2',
          title: 'Improve invitation acceptance rate to 55%',
          description: 'Optimize invitation messaging and targeting',
          completed: false,
          priority: 'high',
          category: 'conversion'
        },
        {
          id: 'step-3',
          title: 'List courses on Coursio Network for reseller distribution',
          description: 'Leverage reseller network to expand reach',
          completed: false,
          priority: 'high',
          category: 'distribution'
        },
        {
          id: 'step-4',
          title: 'Create referral program for existing students',
          description: 'Incentivize word-of-mouth marketing',
          completed: true,
          priority: 'medium',
          category: 'marketing'
        },
        {
          id: 'step-5',
          title: 'Segment audience for targeted campaigns',
          description: 'Personalize outreach based on learner profiles',
          completed: true,
          priority: 'medium',
          category: 'marketing'
        },
        {
          id: 'step-6',
          title: 'Partner with 3 industry influencers',
          description: 'Collaborate with influencers to expand reach',
          completed: false,
          priority: 'high',
          category: 'partnerships'
        }
      ]
    },
    inspiration: {
      benchmarks: [
        {
          metric: 'Invitation acceptance rate',
          yourValue: '45%',
          industryAverage: '52%',
          topPerformers: '68%'
        },
        {
          metric: 'Monthly enrollments',
          yourValue: '25',
          industryAverage: '45',
          topPerformers: '120'
        }
      ],
      successStories: [
        {
          title: 'How to double your enrollment rate with better targeting',
          excerpt: 'Data-driven strategies for invitation campaigns...',
          url: 'https://blog.coursio.com/enrollment-strategies'
        },
        {
          title: 'Leveraging the Coursio Network: A reseller success story',
          excerpt: 'How one creator reached 1000+ students through resellers...',
          url: 'https://blog.coursio.com/reseller-success'
        }
      ],
      marketTrends: [
        'B2B course distribution growing faster than B2C',
        'Corporate training budgets increasing by 15% annually',
        'Reseller networks driving 40% of new enrollments for top creators'
      ]
    }
  }
};

export default MOCK_GOALS_DATA;

